<template>
  <v-app-bar density="compact" class="position-fixed">
    <template #prepend>
      <v-btn
        v-if="!navigationVisibilityStore.navigationToHide"
        icon="mdi-menu"
        @click="navigationVisibilityStore.openSidebars"
      />
      <v-btn
        v-if="navigationVisibilityStore.navigationToHide"
        icon="mdi-close"
        @click="navigationVisibilityStore.hideSidebars"
      />

      <v-card :to="{ name: 'overview' }" width="150" height="48" flat>
        <v-img
          style="margin-top: 6px"
          height="36"
          src="/img/brelag/styler.png"
          class="text-white"
        ></v-img>
      </v-card>
    </template>

    <template #append>
      <v-menu @update:model-value="userMenuOpen = $event">
        <template #activator="{ props }">
          <v-btn color="primary" v-bind="props">
            <template #prepend>
              <v-avatar density="compact" color="primary">
                <v-img
                  v-if="getAvatar()"
                  :src="getAvatar()"
                  width="32"
                  height="32"
                  class="rounded-circle"
                ></v-img>
                <span v-else>{{ initials }}</span>
              </v-avatar>
            </template>
            {{ userName }}
            <template #append>
              <v-badge v-if="hasUnreadInfo" color="red" dot>
                <v-icon :icon="userMenuIcon"></v-icon>
              </v-badge>
              <v-icon v-else :icon="userMenuIcon"></v-icon>
            </template>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(link, index) in profileLinks"
            :key="index"
            :title="link.title"
            @click="link.action"
          >
            <template #prepend>
              <v-badge v-if="link.badge" color="red" dot>
                <v-icon :icon="link.icon"></v-icon>
              </v-badge>
              <v-icon v-else :icon="link.icon"></v-icon>
            </template>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-card width="150" height="48" flat>
        <v-img
          style="margin-top: 6px"
          height="36"
          src="/img/brelag/brelag.png"
          alt="Logo"
        ></v-img>
      </v-card>
    </template>
  </v-app-bar>
  <UpdateNotes v-model="showUpdateNotes" />
</template>

<script setup lang="ts">
import axios from 'axios'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { apiClientV2 } from '@/api/ApiClientV2'
import UpdateNotes from '@/components/auth/UpdateNotes.vue'
import Changelog from '@/docs/Changelog.js'
import { Profile } from '@/models/core/profile'
import { useNavigationVisibilityStore } from '@/store/NavigationVisibilityStore'

const router = useRouter()
const store = useStore()

const { t } = useI18n()

const navigationVisibilityStore = useNavigationVisibilityStore()

const userMenuOpen = ref(false)
const showUpdateNotes = ref(false)
const userMenuIcon = computed(() =>
  userMenuOpen.value ? 'mdi-chevron-up' : 'mdi-chevron-down'
)

const lastUpdateNotesRead = computed(() => {
  return store.state.global.profile.last_update_notes_read
})

const hasUnreadInfo = computed(() => {
  if (lastUpdateNotesRead.value) {
    const lastUpdateRead = new Date(lastUpdateNotesRead.value)
    const lastUpdated = new Date(Changelog.updated)
    return lastUpdateRead < lastUpdated
  } else if (store.state.global.profile.last_update_notes_read === null) {
    return true
  }
  return false
})

const profileLinks = ref({
  changeEmail: {
    title: t('models.auth.changeEmail'),
    icon: 'mdi-email',
    badge: false,
    action: () =>
      router.push({
        name: 'request_change_email',
        params: { id: store.state.global.profileId },
      }),
  },
  changePass: {
    title: t('models.auth.changePassword'),
    icon: 'mdi-lock-outline',
    badge: false,
    action: () => router.push({ name: 'change-password' }),
  },
  showUpdateNotes: {
    title: t('models.auth.showUpdateNotes'),
    icon: 'mdi-information',
    badge: hasUnreadInfo,
    action: async () => {
      showUpdateNotes.value = true
      try {
        await readUpdateNotes()
      } catch (error) {
        console.error('Failed to read update notes:', error)
      }
    },
  },
  logout: {
    title: 'Logout',
    icon: 'mdi-power',
    badge: false,
    action: () => {
      axios
        .post(
          '/api/v1/auth/logout',
          {
            user: {
              id: store.state.global.profileId,
              is_authenticated: store.state.global.is_authenticated,
            },
          },
          store.state.global.axiosConfig
        )
        .then(() => {
          store.dispatch({
            type: 'global/logoutUser',
          })
          router.push('/login')
        })
        .catch((error) => {
          console.warn(error)
        })
    },
  },
})

const userName = computed(() => {
  if (store.state.global.profile.first_name) {
    const firstName = store.state.global.profile.first_name
    const lastName = store.state.global.profile.last_name
    return firstName + ' ' + lastName
  }
  return store.state.global.profile.username
})

const initials = computed(() => {
  if (store.state.global.profile.first_name) {
    const firstName = store.state.global.profile.first_name
    const lastName = store.state.global.profile.last_name
    return firstName.charAt(0) + lastName.charAt(0)
  }
  return store.state.global.profile.username.charAt(0)
})

const readUpdateNotes = async () => {
  const profile = store.state.global.profile
  profile.last_update_notes_read = new Date(Changelog.updated).toISOString()
  await apiClientV2.update<Profile, Profile>(Profile, profile)
}

const getAvatar = () => {
  if (store.state.global.profile) {
    return apiClientV2.getAttachmentUrl(store.state.global.profile.avatar)
  } else {
    return ''
  }
}
</script>

<style lang="scss" scoped>
.switch {
  margin-top: 10px;
}

.profile-dropdown {
  margin-right: 10px;
}

.navbar-dropdown {
  padding-top: 0px;

  .navbar-item {
    padding-left: 0px;
  }
}

// reduce navbar height
.navbar-item,
.navbar-link {
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
}

img.nav-logo {
  height: 100%;
  margin: 5px 20px;
  //margin-top: 5px;
  //margin-left: 20px;
}

.button.is-white.menu-button {
  background-color: white;
  height: 40px;
  width: 40px;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
